import axios from 'axios';

const AUTH_STORAGE_KEY = 'auth';

const isObject = (value) => typeof value === 'object' && value !== null;

const parseJson = (value, fallback) => {
  if (!value) {
    return fallback;
  }

  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    return parsed ?? fallback;
  } catch (error) {
    console.warn('Failed to parse JSON value from storage', error);
    return fallback;
  }
};

const normalizeRoles = (roles) => {
  if (Array.isArray(roles)) {
    return roles;
  }

  const parsed = parseJson(roles, []);
  return Array.isArray(parsed) ? parsed : [];
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  const parsed = parseJson(value, []);
  return Array.isArray(parsed) ? parsed : [];
};

export const getAuthData = () => {
  const raw = localStorage.getItem(AUTH_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const data = JSON.parse(raw);
    if (!isObject(data)) {
      throw new Error('Auth data is not an object');
    }
    return {
      ...data,
      roles: normalizeRoles(data.roles),
    };
  } catch (error) {
    console.warn('Unable to read cached auth data', error);
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return null;
  }
};

export const persistAuthData = (data = {}) => {
  try {
    const existing = getAuthData() || {};
    const merged = {
      ...existing,
      ...data,
    };

    const sanitized = Object.entries(merged).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = key === 'roles' ? normalizeRoles(value) : key === 'affected_store_ids' ? normalizeArray(value) : value;
      }
      return acc;
    }, {});

    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(sanitized));
  } catch (error) {
    console.error('Failed to persist auth data', error);
  }
};

export const buildAuthDataFromSession = () => ({
  token: sessionStorage.getItem('token') || undefined,
  dolibarrToken: sessionStorage.getItem('dolibarrToken') || undefined,
  name: sessionStorage.getItem('name') || undefined,
  roles: normalizeRoles(sessionStorage.getItem('roles')),
  warehouseId: sessionStorage.getItem('warehouseId') || undefined,
  dolitag: sessionStorage.getItem('dolitag') || undefined,
  default_store_id: sessionStorage.getItem('default_store_id') || undefined,
  affected_store_ids: normalizeArray(sessionStorage.getItem('affected_store_ids')),
  userId: sessionStorage.getItem('userId') || undefined,
  parentUser: sessionStorage.getItem('parentUser') || undefined,
});

const setSessionValue = (key, value) => {
  if (value === undefined || value === null) {
    return;
  }

  if (key === 'roles') {
    sessionStorage.setItem('roles', JSON.stringify(normalizeRoles(value)));
    return;
  }

  if (key === 'affected_store_ids') {
    sessionStorage.setItem('affected_store_ids', JSON.stringify(normalizeArray(value)));
    return;
  }

  sessionStorage.setItem(key, String(value));
};

export const hydrateSessionFromAuthData = (authData) => {
  if (!authData) {
    return;
  }

  const {
    token,
    dolibarrToken,
    name,
    roles,
    warehouseId,
    dolitag,
    default_store_id,
    affected_store_ids,
    userId,
    parentUser,
  } = authData;

  setSessionValue('token', token);
  setSessionValue('dolibarrToken', dolibarrToken);
  setSessionValue('name', name);
  setSessionValue('roles', roles);
  setSessionValue('warehouseId', warehouseId);
  setSessionValue('dolitag', dolitag);
  setSessionValue('default_store_id', default_store_id);
  setSessionValue('affected_store_ids', affected_store_ids);
  setSessionValue('userId', userId);
  setSessionValue('parentUser', parentUser);
};

export const syncSessionToLocalStorage = () => {
  persistAuthData(buildAuthDataFromSession());
};

export const clearAuthData = () => {
  localStorage.removeItem(AUTH_STORAGE_KEY);
};

const resolveJwtToken = () => {
  const sessionToken = sessionStorage.getItem('token');
  if (sessionToken) {
    return sessionToken;
  }

  const cached = getAuthData();
  return cached?.token || null;
};

const resolveCachedDolibarrToken = () => {
  const sessionToken = sessionStorage.getItem('dolibarrToken');
  if (sessionToken) {
    return sessionToken;
  }

  const cached = getAuthData();
  return cached?.dolibarrToken || null;
};

export const getDolibarrToken = async () => {
  const cached = resolveCachedDolibarrToken();
  if (cached) {
    return cached;
  }

  const jwtToken = resolveJwtToken();
  if (!jwtToken) {
    return null;
  }

  const apiUrl = process.env.REACT_APP_PEGASUS_API_URL;
  if (!apiUrl) {
    return null;
  }

  try {
    const response = await axios.get(`${apiUrl}/plateforms`, {
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        'Content-Type': 'application/json',
        ENCRYKEY: process.env.REACT_APP_KEY_PEGASUS || '',
      },
    });

    const plateforms = Array.isArray(response?.data) ? response.data : [];
    const dolibarrPlateform = plateforms.find(
      (plateform) => String(plateform?.type || '').toLowerCase() === 'dolibarr'
    );
    const token = dolibarrPlateform?.password || null;

    if (token) {
      sessionStorage.setItem('dolibarrToken', token);
      persistAuthData({ dolibarrToken: token });
    }

    return token;
  } catch (error) {
    console.warn('Unable to resolve dolibarr token', error);
    return null;
  }
};
